/**
 * [번호판 인식 로그 페이지]
 * 목적: 자동 폴링에서 기록된 모든 인식 이벤트를 실시간으로 표시
 *
 * 흐름:
 *   마운트 → 오늘 로그 조회 → 5초마다 자동 갱신
 *   날짜/세차기 필터 변경 → 즉시 재조회
 *
 * 표시 정보:
 *   시각 / 번호판 / 세차기 / 인증 결과(✅ 성공 / ❌ 거부) / 거부 사유 or 회원명+상품 / 신뢰도
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import api from '../api/client';

const WASHER_OPTIONS = ['전체', '세차기1', '세차기2', '세차기3'];
const AUTO_REFRESH_MS = 5000;

/** YYYY-MM-DD 형식으로 오늘 날짜 반환 (로컬 시간 기준) */
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** detectedAt(UTC) → KST HH:MM:SS 문자열 */
function toKSTTime(isoStr) {
  const d = new Date(isoStr);
  return d.toLocaleTimeString('ko-KR', { timeZone: 'Asia/Seoul', hour12: false });
}

/** 거부 사유 → 짧은 라벨 */
const REASON_LABEL = {
  '등록된 차량 없음': '미등록',
  '활성 구독 없음':   '구독 없음',
  '오늘 이용 완료':   '이용 완료',
  '기록 저장 오류':   '저장 오류',
};

export default function LprLogPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [date, setDate]         = useState(todayStr());
  const [washerId, setWasherId] = useState('전체');
  const [logs, setLogs]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchLogs = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ date, limit: '200' });
      if (washerId !== '전체') params.set('washerId', washerId);

      const res = await api.get(`/kiosk/lpr-logs?${params}`);
      setLogs(res.data.logs ?? []);
      setTotal(res.data.total ?? 0);
      setLastUpdated(new Date());
    } catch (err) {
      setError('로그를 불러오지 못했습니다.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [date, washerId]);

  // 필터 변경 시 즉시 조회
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // 5초마다 자동 갱신 (오늘 날짜일 때만)
  useEffect(() => {
    if (date !== todayStr()) return;
    const interval = setInterval(() => fetchLogs(true), AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [date, fetchLogs]);

  // 통계
  const authorized = logs.filter(l => l.authorized).length;
  const denied     = logs.filter(l => !l.authorized).length;

  return (
    <Box>
      {/* ── 헤더 ── */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700} sx={{ whiteSpace: "nowrap" }}>번호판 인식 로그</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {/* 날짜 필터 */}
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            style={{
              border: '1px solid #ccc', borderRadius: 6,
              padding: '6px 10px', fontSize: 14, cursor: 'pointer',
            }}
          />

          {/* 세차기 필터 */}
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>세차기</InputLabel>
            <Select value={washerId} label="세차기" onChange={e => setWasherId(e.target.value)}>
              {WASHER_OPTIONS.map(w => (
                <MenuItem key={w} value={w}>{w}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 갱신 상태 */}
          {date === todayStr() && (
            <Chip
              label="LIVE"
              size="small"
              sx={{
                backgroundColor: '#f44336', color: '#fff', fontWeight: 700,
                animation: 'pulse 1.5s infinite',
                '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.5 }, '100%': { opacity: 1 } },
              }}
            />
          )}
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              갱신: {lastUpdated.toLocaleTimeString('ko-KR', { hour12: false })}
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── 요약 카드 ── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {[
          { label: '전체 인식', value: total, color: '#1976d2', bg: '#e3f2fd' },
          { label: '✅ 인증 성공', value: authorized, color: '#2e7d32', bg: '#e8f5e9' },
          { label: '❌ 인증 거부', value: denied,     color: '#c62828', bg: '#fce4ec' },
        ].map(({ label, value, color, bg }) => (
          <Paper key={label} elevation={0} sx={{ flex: 1, px: 2.5, py: 1.5, backgroundColor: bg, border: `1px solid ${color}22` }}>
            <Typography variant="caption" sx={{ color, fontWeight: 600 }}>{label}</Typography>
            <Typography variant="h5" fontWeight={800} sx={{ color }}>{value}</Typography>
          </Paper>
        ))}
      </Box>

      {/* ── 에러 ── */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── 모바일: 카드 리스트 / 데스크탑: 테이블 ── */}
      {isMobile ? (
        <Stack spacing={1.25}>
          {loading ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Paper>
          ) : logs.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              인식 기록이 없습니다
            </Paper>
          ) : (
            logs.map((log) => (
              <Paper
                key={log._id}
                elevation={0}
                sx={{
                  p: 1.5,
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  backgroundColor: log.authorized ? '#fff' : '#fff8f8',
                }}
              >
                {/* 상단: 결과 아이콘 + 번호판 + 신뢰도 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {log.authorized ? (
                    <CheckCircleIcon sx={{ color: '#43a047', fontSize: 22 }} />
                  ) : (
                    <CancelIcon sx={{ color: '#e53935', fontSize: 22 }} />
                  )}
                  <Typography
                    fontWeight={700}
                    fontFamily="monospace"
                    fontSize={17}
                    letterSpacing={1}
                    sx={{ flex: 1 }}
                  >
                    {log.plate}
                  </Typography>
                  {log.confidence != null && (
                    <Typography
                      fontSize={13}
                      fontWeight={700}
                      sx={{ color: log.confidence >= 0.9 ? '#43a047' : '#f57c00' }}
                    >
                      {Math.round(log.confidence * 100)}%
                    </Typography>
                  )}
                </Box>

                {/* 중간: 시각 + 세차기 칩 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary">
                    {toKSTTime(log.detectedAt)}
                  </Typography>
                  <Chip label={log.washerId} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                </Box>

                {/* 하단: 상세 (회원/상품 또는 거부 사유) */}
                {log.authorized ? (
                  <Typography fontSize={13}>
                    <span style={{ fontWeight: 600 }}>{log.memberName}</span>
                    {log.productName && (
                      <span style={{ color: '#888', marginLeft: 6 }}>· {log.productName}</span>
                    )}
                  </Typography>
                ) : (
                  <Chip
                    label={REASON_LABEL[log.reason] ?? log.reason ?? '알 수 없음'}
                    size="small"
                    sx={{
                      fontSize: 11,
                      backgroundColor: '#fce4ec',
                      color: '#c62828',
                      fontWeight: 600,
                    }}
                  />
                )}
              </Paper>
            ))
          )}
        </Stack>
      ) : (
        <TableContainer component={Paper} elevation={1} sx={{ borderRadius: '8px' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: '#f5f5f5' } }}>
                <TableCell>시각</TableCell>
                <TableCell>번호판</TableCell>
                <TableCell>세차기</TableCell>
                <TableCell align="center">결과</TableCell>
                <TableCell>상세</TableCell>
                <TableCell align="right">신뢰도</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    인식 기록이 없습니다
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow
                    key={log._id}
                    hover
                    sx={{
                      backgroundColor: log.authorized ? 'transparent' : '#fff8f8',
                    }}
                  >
                    {/* 시각 */}
                    <TableCell sx={{ color: 'text.secondary', fontSize: 13, whiteSpace: 'nowrap' }}>
                      {toKSTTime(log.detectedAt)}
                    </TableCell>

                    {/* 번호판 */}
                    <TableCell>
                      <Typography fontWeight={700} fontFamily="monospace" fontSize={15} letterSpacing={1}>
                        {log.plate}
                      </Typography>
                    </TableCell>

                    {/* 세차기 */}
                    <TableCell>
                      <Chip label={log.washerId} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                    </TableCell>

                    {/* 결과 아이콘 */}
                    <TableCell align="center">
                      {log.authorized ? (
                        <CheckCircleIcon sx={{ color: '#43a047', fontSize: 20 }} />
                      ) : (
                        <CancelIcon sx={{ color: '#e53935', fontSize: 20 }} />
                      )}
                    </TableCell>

                    {/* 상세 */}
                    <TableCell>
                      {log.authorized ? (
                        <Typography fontSize={13}>
                          <span style={{ fontWeight: 600 }}>{log.memberName}</span>
                          {log.productName && (
                            <span style={{ color: '#888', marginLeft: 6 }}>· {log.productName}</span>
                          )}
                        </Typography>
                      ) : (
                        <Chip
                          label={REASON_LABEL[log.reason] ?? log.reason ?? '알 수 없음'}
                          size="small"
                          sx={{
                            fontSize: 11,
                            backgroundColor: '#fce4ec',
                            color: '#c62828',
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </TableCell>

                    {/* 신뢰도 */}
                    <TableCell align="right">
                      {log.confidence != null ? (
                        <Typography
                          fontSize={13}
                          fontWeight={600}
                          sx={{ color: log.confidence >= 0.9 ? '#43a047' : '#f57c00' }}
                        >
                          {Math.round(log.confidence * 100)}%
                        </Typography>
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
